import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/AuthContext";
import { Splash } from "../../components/layout/Splash";
import { Button, ButtonAnchor } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { AlertIcon, CheckIcon, ClockIcon, InfoIcon, ShieldIcon } from "../../components/ui/icons";
import { cn } from "../../lib/cn";
import { config } from "../../lib/config";
import {
  fetchGuideRequirements,
  fetchMyGuideStatus,
  type GuideRequirements,
} from "../../lib/guide-api";
import {
  landingAfterSubmit,
  probeFromError,
  probeFromStatus,
  resolveLanding,
  type IneligibleReason,
  type InsuranceState,
  type Landing,
} from "./landing";
import { OnboardingWizard, RegistrationProgress, type SubmittedResult } from "./OnboardingWizard";

// /onboarding（G-02 / SCR-07）の入口。ガイドの状態を読んで居場所を振り分ける（tst-00cu.5）。
//
//   未提出（404 / Applying）   → OnboardingWizard（本登録ウィザード）
//   提出済み・承認待ち          → PendingApproval（審査中: 進捗・保険の状態・目安・問い合わせ先）
//   Active                      → AccountActive（guide-app へ渡す橋）
//   Inactive / Terminated / 403 → NotEligible（案内文のみ）
//   判定できない                → LoadFailed（再試行。ウィザードへは落とさない）
//
// 判定は landing.ts（純関数・vitest）。ここは取得と描画だけを担う。
//
// 保険証券を求めるかどうか（GET /guides/me/requirements / tst-szv6.5）も状態と一緒に読む。
// 読めなかったときは LoadFailed にする — 既定値で進めると、求めていない書類を必須にするか、
// 求めている書類を集めずに提出させるかのどちらかになる。
// ウィザード完了直後も PendingApproval を出して留まる（以前の step 6「Complete」の常設化）。
// 提出済みのアカウントにウィザードが描画されることが無いので、再送信の 409 は構造的に起きない。

type State =
  | { kind: "loading" }
  | {
      kind: "ready";
      landing: Landing;
      /** 読めなかったとき（landing.view="error"）は null。 */
      requirements: GuideRequirements | null;
      justSubmitted: SubmittedResult | null;
    };

export function Onboarding() {
  const { guide, signOut } = useAuth();
  const [state, setState] = useState<State>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    // 状態の取得失敗（404 / 403 を含む）は probe に畳んで landing が受ける。
    // 401 は api() が signOut 済み → RequireAuth が /login へ退避する。
    const [probe, requirements] = await Promise.all([
      fetchMyGuideStatus().then(probeFromStatus, probeFromError),
      fetchGuideRequirements().catch(() => null),
    ]);
    setState({
      kind: "ready",
      landing: requirements ? resolveLanding(probe, requirements) : { view: "error" },
      requirements,
      justSubmitted: null,
    });
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") return <Splash />;

  const { landing } = state;
  switch (landing.view) {
    case "wizard": {
      const requirements = { insuranceRequired: landing.insuranceRequired };
      return (
        <OnboardingWizard
          insuranceRequired={landing.insuranceRequired}
          onSubmitted={(result) =>
            setState({
              kind: "ready",
              landing: landingAfterSubmit(result, requirements),
              requirements,
              justSubmitted: result,
            })
          }
          onAlreadyRegistered={() => void load()}
        />
      );
    }
    case "pending":
      return (
        <PendingApproval
          email={guide?.email}
          insurance={landing.insurance}
          justSubmitted={state.justSubmitted}
          onRefresh={() => void load()}
          onSignOut={signOut}
        />
      );
    case "active":
      return <AccountActive guideAppBaseUrl={config.guideAppBaseUrl} onSignOut={signOut} />;
    case "ineligible":
      return <NotEligible reason={landing.reason} onSignOut={signOut} />;
    case "error":
      return <LoadFailed onRetry={() => void load()} onSignOut={signOut} />;
  }
}

// ── 共通の枠 ─────────────────────────────────────────────────────────

function Shell({
  title,
  lead,
  badge,
  children,
}: {
  title: string;
  lead: string;
  badge?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="min-h-dvh bg-canvas px-5 py-8">
      <div className="mx-auto w-full max-w-lg">
        <header className="flex items-start justify-between gap-3">
          <div>
            <div className="eyebrow mb-1">Tokyo Signature Tours</div>
            <h1 className="text-3xl font-bold tracking-tight text-ink">{title}</h1>
            <p className="mt-2 max-w-xs text-sm text-muted">{lead}</p>
          </div>
          {badge}
        </header>
        {children}
      </div>
    </div>
  );
}

function Badge({
  tone,
  icon,
  children,
}: {
  tone: "accent" | "success";
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-pill px-3 py-1.5 text-xs font-medium",
        tone === "success" ? "bg-success-soft text-success" : "bg-accent-soft text-accent",
      )}
    >
      {icon}
      {children}
    </span>
  );
}

function SupportLine() {
  return (
    <p className="mt-4 text-sm text-muted">
      Questions? Contact{" "}
      <a href={`mailto:${config.supportEmail}`} className="font-medium text-accent hover:underline">
        {config.supportEmail}
      </a>
      .
    </p>
  );
}

// ── 審査中（Onboarding / 提出直後） ────────────────────────────────────

const INSURANCE_LABEL: Record<InsuranceState, { text: string; cls: string; note: string }> = {
  not_submitted: {
    text: "Not submitted",
    cls: "bg-warning/10 text-warning",
    note: "An approved liability insurance certificate is required before you can receive assignments. Contact TST support to submit it.",
  },
  pending: {
    text: "Pending review",
    cls: "bg-accent-soft text-accent",
    note: "An administrator verifies the certificate as part of your approval.",
  },
  approved: {
    text: "Approved",
    cls: "bg-success-soft text-success",
    note: "Your certificate has been verified.",
  },
  rejected: {
    text: "Rejected",
    cls: "bg-danger/10 text-danger",
    note: "The certificate could not be accepted. Contact TST support to submit a corrected one.",
  },
};

function PendingApproval({
  email,
  insurance,
  justSubmitted,
  onRefresh,
  onSignOut,
}: {
  email?: string;
  /** null は保険証券を求めていない（保留中 / tst-szv6.5）＝保険の行・文言を出さない。 */
  insurance: InsuranceState | null;
  justSubmitted: SubmittedResult | null;
  onRefresh: () => void;
  onSignOut: () => void;
}) {
  const ins = insurance ? INSURANCE_LABEL[insurance] : null;
  // 提出直後の確認文。保険アップロード失敗時（warning あり）と、保険証券を求めていないときは
  // 提出済みリストから外して誤認を避ける。
  const submittedItems =
    justSubmitted?.warning || !ins
      ? "contract, anti-social confirmation, bank details, and emergency contact"
      : "contract, anti-social confirmation, insurance certificate, bank details, and emergency contact";

  return (
    <Shell
      title="Application under review"
      lead="Your registration is complete. An administrator is reviewing it before your account is activated."
      badge={
        <Badge tone="accent" icon={<ClockIcon className="size-3.5" />}>
          Awaiting approval
        </Badge>
      }
    >
      {justSubmitted && (
        <Card className="mt-6 p-5">
          <div className="flex items-start gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-success-soft text-success">
              <CheckIcon className="size-5" />
            </span>
            <div>
              <h2 className="text-base font-bold text-ink">Registration submitted</h2>
              <p className="mt-1 text-sm text-muted">
                Your {submittedItems} have been submitted{email ? ` for ${email}` : ""}.
              </p>
            </div>
          </div>
          {justSubmitted.warning && (
            <div className="mt-4 flex items-start gap-2 rounded-md bg-warning/10 p-3 text-sm text-warning">
              <AlertIcon className="mt-0.5 size-4 shrink-0" />
              <div>
                <div className="font-semibold">Insurance certificate: Pending</div>
                <p className="mt-0.5">{justSubmitted.warning}</p>
              </div>
            </div>
          )}
        </Card>
      )}

      <Card className={cn("p-5", justSubmitted ? "mt-4" : "mt-6")}>
        <h3 className="text-sm font-semibold text-ink">Guide registration progress</h3>
        <RegistrationProgress current="Approval" />
      </Card>

      <Card className="mt-4 p-5">
        <h3 className="text-sm font-semibold text-ink">Status</h3>
        <ul className="mt-3 divide-y divide-line">
          <li className="flex items-center justify-between py-3">
            <span className="text-sm text-ink">Account approval</span>
            <span className="rounded-pill bg-accent-soft px-2.5 py-1 text-[11px] font-medium text-accent">
              Pending
            </span>
          </li>
          {ins && (
            <li className="py-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-ink">Insurance certificate</span>
                <span className={cn("rounded-pill px-2.5 py-1 text-[11px] font-medium", ins.cls)}>
                  {ins.text}
                </span>
              </div>
              <p className="mt-1.5 text-xs text-muted">{ins.note}</p>
            </li>
          )}
        </ul>
      </Card>

      <Card className="mt-4 p-5">
        <div className="flex items-start gap-3">
          <InfoIcon className="mt-0.5 size-5 shrink-0 text-accent" />
          <div className="text-sm text-ink-soft">
            <div className="font-semibold text-ink">What happens next</div>
            <ul className="mt-2 space-y-1.5">
              <li className="flex gap-2">
                <span className="text-accent">•</span>
                An administrator reviews your {ins ? "documents and insurance" : "documents"},
                usually within a few business days.
              </li>
              <li className="flex gap-2">
                <span className="text-accent">•</span>
                Once approved, you will receive an email and can sign in to the Guide app to receive
                assignment requests.
              </li>
              <li className="flex gap-2">
                <span className="text-accent">•</span>
                No action is needed from you in the meantime.
              </li>
            </ul>
            <SupportLine />
          </div>
        </div>
      </Card>

      <div className="mt-5 flex gap-3">
        <Button variant="outline" size="block" onClick={onSignOut}>
          Sign out
        </Button>
        <Button size="block" onClick={onRefresh}>
          Refresh status
        </Button>
      </div>
    </Shell>
  );
}

// ── Active → guide-app への橋 ────────────────────────────────────────

function AccountActive({
  guideAppBaseUrl,
  onSignOut,
}: {
  guideAppBaseUrl: string;
  onSignOut: () => void;
}) {
  return (
    <Shell
      title="Your account is active"
      lead="Registration is complete. Day-to-day work — assignment requests, tours, and your profile — happens in the Guide app."
      badge={
        <Badge tone="success" icon={<CheckIcon className="size-3.5" />}>
          Active
        </Badge>
      }
    >
      <Card className="mt-6 p-5">
        <RegistrationProgress current="done" />
      </Card>

      <Card className="mt-4 p-5">
        <h2 className="text-base font-bold text-ink">Open the Guide app</h2>
        <p className="mt-1 text-sm text-muted">
          Sign in with the same email address. This registration site has nothing further for you to
          do.
        </p>
        {/* 別配信（guide-app）なので SPA 内 Link ではなく通常の anchor。 */}
        <ButtonAnchor href={`${guideAppBaseUrl}/login`} size="block" className="mt-4">
          Open the Guide app
        </ButtonAnchor>
        <SupportLine />
      </Card>

      <div className="mt-5">
        <Button variant="outline" size="block" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </Shell>
  );
}

// ── 稼働できないアカウント ────────────────────────────────────────────

const INELIGIBLE_TEXT: Record<IneligibleReason, { title: string; body: string }> = {
  inactive: {
    title: "Your guide account is inactive",
    body: "This account is currently paused and cannot receive assignments. If you believe this is a mistake or want to resume guiding, please contact TST support.",
  },
  terminated: {
    title: "Your guide agreement has ended",
    body: "This account is no longer active with Tokyo Signature Tours. If you have questions about your agreement, please contact TST support.",
  },
  barred: {
    title: "Your guide account is not active",
    body: "This account cannot be used at the moment. Please contact TST support for details.",
  },
  forbidden: {
    title: "This account cannot be used here",
    body: "Guide registration is only available to accepted applicants signed in with their guide account. If you were accepted and see this message, please contact TST support.",
  },
  unknown_status: {
    title: "We could not determine your account status",
    body: "Please contact TST support so we can check your registration.",
  },
};

function NotEligible({ reason, onSignOut }: { reason: IneligibleReason; onSignOut: () => void }) {
  const t = INELIGIBLE_TEXT[reason];
  return (
    <Shell title={t.title} lead="No further steps are available on this site.">
      <Card className="mt-6 p-5">
        <div className="flex items-start gap-3">
          <ShieldIcon className="mt-0.5 size-5 shrink-0 text-muted" />
          <div className="text-sm text-ink-soft">
            <p>{t.body}</p>
            <SupportLine />
          </div>
        </div>
      </Card>
      <div className="mt-5">
        <Button variant="outline" size="block" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </Shell>
  );
}

// ── 判定できない ─────────────────────────────────────────────────────

function LoadFailed({ onRetry, onSignOut }: { onRetry: () => void; onSignOut: () => void }) {
  return (
    <Shell
      title="Something went wrong"
      lead="We could not load your registration status. Please try again in a moment."
    >
      <Card className="mt-6 p-5">
        <div className="flex items-start gap-3 text-sm text-ink-soft">
          <AlertIcon className="mt-0.5 size-5 shrink-0 text-danger" />
          <div>
            <p>
              If this keeps happening, contact TST support — do not re-submit your registration.
            </p>
            <SupportLine />
          </div>
        </div>
      </Card>
      <div className="mt-5 flex gap-3">
        <Button variant="outline" size="block" onClick={onSignOut}>
          Sign out
        </Button>
        <Button size="block" onClick={onRetry}>
          Try again
        </Button>
      </div>
    </Shell>
  );
}
